import { createSlice } from '@reduxjs/toolkit'

import { logoutUser } from '../actions/userActions'
import { createLoadingReducer } from './shared'
import { AdPod } from 'common/api/v1/types'
import { clearAdPod, deleteAdPod, deleteAdPods, getAdPod, listAdPods, updateAdPod } from '../actions/adPodActions'
import type { ApplicationException } from '../../api/ApplicationException'

interface State {
  adPods: Array<AdPod>
  selectedAdPod?: AdPod
  total: number
  loading: boolean
  saving: boolean
  formErrors?: ApplicationException['errorInfo']['details']
}

export const initialAdPods: State = {
  adPods: [],
  selectedAdPod: undefined,
  total: 0,
  loading: false,
  saving: false,
  formErrors: undefined,
}

const { isLoadingAction, loadingReducer } = createLoadingReducer<State>(listAdPods)

const adPodSlice = createSlice({
  name: 'adPod',
  initialState: initialAdPods,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdPod.fulfilled, (state, { payload: selectedAdPod }): State => ({ ...state, selectedAdPod }))
      .addCase(clearAdPod, (state): State => ({ ...state, selectedAdPod: undefined }))

      .addCase(updateAdPod.pending, (state): State => ({ ...state, saving: true }))
      .addCase(updateAdPod.rejected, (state): State => ({ ...state, saving: false }))
      .addCase(
        updateAdPod.fulfilled,
        (state, { payload: adPod }): State => ({
          ...state,
          adPods: [adPod, ...state.adPods.filter((o) => o.id !== adPod.id)],
        }),
      )
      .addCase(deleteAdPod.pending, (state): State => ({ ...state, saving: true }))
      .addCase(deleteAdPod.rejected, (state): State => ({ ...state, saving: false }))
      .addCase(
        deleteAdPod.fulfilled,
        (state, { payload: e }): State => ({
          ...state,
          adPods: state.adPods.filter((o) => o.id !== e.id),
          saving: false,
        }),
      )
      .addCase(
        deleteAdPods.fulfilled,
        (state, { payload: ids }): State => ({
          ...state,
          adPods: state.adPods.filter((o) => !ids.includes(o.id)),
          saving: false,
        }),
      )
      .addCase(
        listAdPods.fulfilled,
        (state, { payload: adPods }): State => ({
          ...state,
          adPods: adPods.items,
          total: adPods.total,
          loading: false,
        }),
      )
      .addCase(logoutUser.fulfilled, (): State => initialAdPods)
      .addMatcher(isLoadingAction, loadingReducer)
  },
})

export default adPodSlice.reducer
